import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { RootComponent } from './root/root.component';
import { OrderStatusComponent } from './order-status/order-status.component';
import { MasterStoreComponent } from './store-lazy/master-store/master-store.component';
import { MenuItemMasterComponent } from './menu-item/menu-item-master/menu-item-master.component';
import { OrderSummaryMasterComponent } from './order-summary/order-summary-master/order-summary-master.component';
import { OffersComponent } from './offers/offers.component';
import { OfferDetailComponent } from './offers/offer-detail/offer-detail.component';
import { OfferSummaryComponent } from './offers/offer-summary/offer-summary.component';
import { OfferStatusComponent } from './offers/offer-status/offer-status.component';
import { MyOffersComponent } from './offers/my-offers/my-offers.component';
import { RedeemComponent } from './offers/redeem/redeem.component';
import { RedeemStatusComponent } from './offers/redeem-status/redeem-status.component';
import { RedeemHistoryComponent } from './offers/redeem-history/redeem-history.component';
import { OfferV2Component } from './offer-v2/offer-v2.component';
import { PendingChangesGuard } from './_helpers/pending-changes-gaurd';
import { WalletComponent } from './order-summary/wallet/wallet.component';
import {ThreedsCheckComponent} from './order-summary/threeds-check/threeds-check.component';
import { WalletCardDetailComponent } from './order-summary/wallet/wallet-card-detail/wallet-card-detail.component';
import { SendGiftCardComponent } from './order-summary/wallet/send-gift-card/send-gift-card.component';
import { OrderSuspiciousComponent } from './order-suspicious/order-suspicious.component';
import { OrderSubmittedGuard } from './_helpers/order-submitted.guard';
import { OrderTrackingDemoComponent } from './order-status/delivery-tracking/demo/order-tracking-demo.component';

const routes: Routes = [
  { path: '', component: RootComponent, data: { pageTitle: 'Labrador AI' } },

  // ── Order workflow ──────────────────────────────────────────────────────────
  {
    // No-login demo of delivery tracking with simulated data (ENG-5514)
    path: 'order-tracking-demo',
    component: OrderTrackingDemoComponent,
    data: { pageTitle: 'Order Tracking Demo' }
  },
  {
    path: 'order/:o_hash/status/suspicious',
    component: OrderSuspiciousComponent,
  },
  {
    path: 'order/:o_hash/status/:status',
    component: OrderStatusComponent,
    data: { pageTitle: 'Order Submitted' },
  },
  {
    path: 'order/:orderId',
    component: OrderSummaryMasterComponent,
    canActivate: [OrderSubmittedGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'order/:orderId/tds',
    component: ThreedsCheckComponent,
  },
  {
    path: 'order/:orderId/wallet',
    component: WalletComponent,
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'order/:orderId/wallet/:cardId',
    component: WalletCardDetailComponent,
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'order/:orderId/store/:storeId/catering',
    loadChildren: () => import('./catering/catering.module').then(m => m.CateringModule),
  },
  {
    path: 'order/:orderId/store/:storeId',
    component: MasterStoreComponent,
    canActivate: [OrderSubmittedGuard],
  },
  {
    path: 'order/:orderId/store/:storeId/menu-item-group/:menuItemGroupId',
    component: MenuItemMasterComponent,
    canActivate: [OrderSubmittedGuard],
  },
  {
    path: 'order/:orderId/store/:storeId/menu-item-group/:menuItemGroupId/menu-item/:menuItemId/parsed-menu-item/:parsedMenuItem/modifiers',
    component: MenuItemMasterComponent,
    canActivate: [OrderSubmittedGuard],
  },
  {
    path: 'order/:orderId/store/:storeId/order-offers',
    component: OfferV2Component,
    canActivate: [OrderSubmittedGuard],
  },

  // ── Store ───────────────────────────────────────────────────────────────────
  {
    path: 'store/:storeId',
    component: MasterStoreComponent,
    data: { pageTitle: 'Master Store' },
  },
  {
    path: 'store/:storeId/menu-item-group/:menuItemGroupId',
    component: MenuItemMasterComponent,
    canActivate: [OrderSubmittedGuard],
    data: { pageTitle: 'Master MenuItem' },
  },
  {
    path: 'store/:storeId/offers/order/:offerOrderId',
    component: OfferSummaryComponent,
  },
  {
    path: 'store/:storeId/offers',
    component: OffersComponent,
  },
  {
    path: 'store/:storeId/offers/order/:offerOrderId/available',
    component: OffersComponent,
  },
  {
    path: 'store/:storeId/offers/order/:offerOrderId/offer/:offerId',
    component: OfferDetailComponent,
  },
  {
    path: 'store/:storeId/offers/:offerId',
    component: OfferDetailComponent,
  },
  {
    path: 'store/:storeId/offer/order/:offerOrderId/status/:status',
    component: OfferStatusComponent,
  },
  {
    path: 'store/:storeId/:hash/my-offers',
    component: MyOffersComponent,
  },
  {
    path: 'store/:storeId/:hash/wallet',
    component: WalletComponent,
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'store/:storeId/:hash/send-gift',
    component: SendGiftCardComponent,
  },
  {
    path: 'store/:storeId/:hash/wallet/:cardId',
    component: WalletCardDetailComponent,
  },
  {
    path: 'store/:storeId/:hash/my-offers/history',
    component: RedeemHistoryComponent,
    data: { pageTitle: 'Offer Submitted' },
  },
  {
    path: 'store/:storeId/order-offers',
    component: OfferV2Component,
  },

  // ── Redeem / Gift (non-lazy, part of core offers flow) ─────────────────────
  {
    path: ':hash/my-offers/:offerId/redeem',
    component: RedeemComponent,
  },
  {
    path: ':hash/my-offers/:offerId/redeem/:redeemId/status',
    component: RedeemStatusComponent,
    data: { pageTitle: 'Offer Submitted' },
  },

  // ── Lazy: reservation widget (store/:storeId/reserve) ──────────────────────
  {
    path: 'store/:storeId/reserve',
    loadChildren: () =>
      import('./reserve/reserve.module').then((m) => m.ReserveModule),
  },

  // ── Lazy: catering menu (store/:storeId/catering) ───────────────────────────
  {
    path: 'store/:storeId/catering',
    loadChildren: () => import('./catering/catering.module').then(m => m.CateringModule)
  },

  // ── QA/demo: catering access gate with canned data, no login (?code=YOURCODE)
  {
    path: 'catering-gate-demo',
    loadComponent: () => import('./catering/catering-gate-demo.component').then(m => m.CateringGateDemoComponent)
  },

  // ── Lazy: manage reservation by code (/r/:code) ─────────────────────────────
  {
    path: 'r',
    loadChildren: () =>
      import('./reserve/reserve.module').then((m) => m.ReserveModule),
  },

  // ── Lazy: store/* catch-all (loyalty, history, referral under store) ────────
  // MUST come after all explicit store/:storeId/... routes above
  {
    path: 'store',
    loadChildren: () =>
      import('./store-features/store-features.module').then(
        (m) => m.StoreFeaturesModule
      ),
  },

  // ── Lazy: gift ──────────────────────────────────────────────────────────────
  {
    path: 'gift',
    loadChildren: () =>
      import('./offers/gift.module').then((m) => m.GiftModule),
  },

  // ── Lazy: referral reward (referral/:key/...) ───────────────────────────────
  {
    path: 'referral',
    loadChildren: () =>
      import('./referral/referral-reward.module').then(
        (m) => m.ReferralRewardModule
      ),
  },

  // ── Lazy: worldnet ──────────────────────────────────────────────────────────
  {
    path: 'worldnet',
    loadChildren: () =>
      import('./worldnet/worldnet.module').then((m) => m.WorldnetModule),
  },

  // ── Lazy: invoice pay ───────────────────────────────────────────────────────
  {
    path: 'pay',
    loadChildren: () =>
      import('./invoice-pay/invoice.module').then((m) => m.InvoiceModule),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
